import { alpha } from '@mui/material/styles';
import {
  MOTION,
  RADIUS,
  UNIVERSE_FONT,
  accentOf,
  fieldLineOf,
  focusRingSx,
  hairlineOf
} from 'src/theme/universe/brand';
import {
  primaryButtonSx,
  secondaryButtonSx
} from 'src/theme/universe/buttonSx';

// ----------------------------------------------------------------------
// Auth branding — LingoYou Group S.r.l. palette.
//
// Straight from the brand manual's colour page, no sampled or invented values:
//   Celestial Blue   #499AD4
//   Cadmium Violet   #784593
//   Black Olive      #3C3C3B   (the corporate ink)
// and the LingoYou Group gradient, which runs blue → violet.
//
// One palette, three identities: each portal owns a hue drawn from it (see the
// AUTH_PORTALS comment below), and carries that hue from the chooser card all
// the way through its sign-in panel and buttons.
// ----------------------------------------------------------------------

export const CELESTIAL_BLUE = '#499AD4';
export const CADMIUM_VIOLET = '#784593';
export const BLACK_OLIVE = '#3C3C3B';

/** The LingoYou Group S.r.l. gradient. */
export const GROUP_GRADIENT = `linear-gradient(135deg, ${CELESTIAL_BLUE} 0%, ${CADMIUM_VIOLET} 100%)`;

// Tints and shades of the three brand colours. Same hues, different lightness —
// standard practice, and the only honest way to get three distinct identities
// out of a two-colour-plus-ink palette. The *_INK values are also what text and
// borders use on light surfaces, where #499AD4 and #784593 fail contrast.
export const BLUE_LIGHT = '#6BB0E0';
export const VIOLET_BRIGHT = '#A87BC2';
export const BLUE_INK = '#2C6A99';
export const VIOLET_LIGHT = '#9058AB';
export const VIOLET_INK = '#5A3370';

// The exact midpoint of the Group gradient — the colour where Celestial Blue
// becomes Cadmium Violet, rgb(96,111,179). Not a new hue: it is already in the
// brand gradient, just named so the intranet can use it for solid fills.
export const INDIGO = '#606FB3';
export const INDIGO_BRIGHT = '#8A95D6';
export const INDIGO_INK = '#414D85';

// The official LingoYou Universe lock-up, used exactly as the brand pack
// supplies it — the White version on dark grounds, the Colour version on light.
// An earlier attempt recoloured the artwork's gradient onto the Group palette;
// that was wrong. The logo is fixed artwork, not a themeable component.
export const BRAND_LOGO_WHITE = '/logo/universe-white.svg';
export const BRAND_LOGO_COLOR = '/logo/universe-color.svg';
// Icon-only globe (no wordmark), from the brand pack — for the collapsed rail
// and the favicon, where the lock-up would be illegible.
export const BRAND_ICON_WHITE = '/logo/universe-icon-white.svg';
export const BRAND_ICON_COLOR = '/logo/universe-icon-color.svg';

// One identity per portal, all of it colour:
//
//   Clients    Celestial Blue   light blue → deep blue
//   Team       the Group gradient, blue → violet (the full brand mark)
//   Linguists  Cadmium Violet   light violet → deep violet
//
// Black Olive is NOT used as a panel colour. It is the manual's ink — using it
// as a fill made the intranet a sheet of grey, which is exactly what it looks
// like. It stays where it belongs: text.
//
// The three read as blue, blue-violet and violet — a clear progression rather
// than two lookalikes and an outlier.
//
// `accent` drives the solid primary button, the focus ring and the links, so
// the button belongs to the panel beside it. The gradient stays exclusive to
// the panel — one gradient, one solid, everything else neutral.

export const AUTH_PORTALS = {
  client: {
    key: 'client',
    name: 'Client',
    label: 'Clients',
    path: '/clients',
    mascot: '/assets/mascots/planet11-card.png',
    blurb: 'Follow your translation projects and talk to your team.',
    heroTitle: 'Your projects, tracked and translated.',
    gradient: `linear-gradient(135deg, ${BLUE_LIGHT} 0%, ${BLUE_INK} 100%)`,
    accent: CELESTIAL_BLUE,
    accentBright: BLUE_LIGHT,
    accentDeep: BLUE_INK,
    accentInk: '#FFFFFF'
  },
  intranet: {
    key: 'intranet',
    name: 'Intranet',
    label: 'Team',
    path: '/intranet',
    mascot: '/assets/mascots/planet21-card.png',
    // Two surfaces, two lengths. `blurb` is the chooser card, where the three
    // portals sit side by side and a line that wraps to three while its
    // neighbours wrap to two is the only thing you see; `heroBlurb` is the
    // sign-in panel, which has the room for the full sentence.
    blurb: 'Quotes, projects, linguists and invoicing.',
    heroBlurb:
      "Quotes, projects, linguists and invoicing — the LingoYou team's workspace.",
    // The owner, 08/10/2026, on "Everything your team needs, in one place. /
    // The internal workspace for the LingoYou team.": "I DON'T LIKE THIS
    // SENTENCE". It was the SaaS promise every product makes — true of a
    // to-do list and of an ERP alike, so it said nothing about this one. This
    // pair names the company's actual subject, languages, and ties it to the
    // product's own name.
    // The hard space is load-bearing: left to itself the line broke as
    // "Every language, one / Universe." and left the product's own name
    // orphaned on the second line. It breaks after the comma now.
    heroTitle: 'Every language, one Universe.',
    // The full LingoYou Group gradient — both brand colours, edge to edge. The
    // internal tool is the one place the complete brand mark belongs, and it
    // sits naturally between the blue clients panel and the violet linguists
    // one without repeating either.
    gradient: `linear-gradient(135deg, ${CELESTIAL_BLUE} 0%, ${CADMIUM_VIOLET} 100%)`,
    accent: INDIGO,
    accentBright: INDIGO_BRIGHT,
    accentDeep: INDIGO_INK,
    accentInk: '#FFFFFF'
  },
  vendor: {
    key: 'vendor',
    name: 'Vendor',
    label: 'Vendors',
    path: '/vendors',
    mascot: '/assets/mascots/planet31-card.png',
    blurb: 'Assignments, deliveries and payments for vendors.',
    heroTitle: 'All your work, in one place.',
    gradient: `linear-gradient(135deg, ${VIOLET_LIGHT} 0%, ${VIOLET_INK} 100%)`,
    accent: CADMIUM_VIOLET,
    accentBright: VIOLET_BRIGHT,
    accentDeep: VIOLET_INK,
    accentInk: '#FFFFFF'
  }
};

export const AUTH_PORTAL_LIST = [
  AUTH_PORTALS.client,
  AUTH_PORTALS.intranet,
  AUTH_PORTALS.vendor
];

// Montserrat is the manual's primary typeface; loaded in public/index.html.
export const BRAND_FONT = '"Montserrat", "Public Sans", sans-serif';


// ---------------------------------------------------------------------------
// THE AUTH FORM, on the Universe tokens (DESIGN.md §16, §17, §18).
//
// Six forms across three portals share these three recipes — sign in, the
// sign-in link, forgot password, set a new password, register, accept an
// invitation. They used to be hand-rolled here in the LingoYou Group blue;
// they are now the system's field, the system's pill button and the system's
// link, read from `src/theme/universe/brand.js`, which is what §28 means by
// `portals.js` re-exporting the new tokens under the old names.
//
// Each takes the THEME, not a portal: the three portals share one identity
// (§28 DECISION 3) and differ by wordmark and copy. A call site that still
// passes a portal object gets the same result, which is the point.
//
// Auth is the one place §17 allows the large button, and §16's "prominent"
// control answers it — a sign-in field at a list's 36px reads as a filter.
//
// ONE HEIGHT FOR EVERY CONTROL ON THE CARD, 44px (2026-10-11, the owner: the
// card "feels too short and vertically compressed"). It was 40 for the fields
// and, through the large button's own padding, 48 for the button: two
// heights in one column, and neither the 44px a thumb needs on the phone the
// sign-in is most often opened on. The fields, the primary, the Microsoft
// button and every other auth action now share it, so the column reads as one
// rhythm and the visual size IS the touch target (DESIGN.md §17).
// ---------------------------------------------------------------------------

/** The height of every control on an auth card. */
export const AUTH_CONTROL = 44;

/**
 * THE SHORT WINDOWS, where the card's rhythm steps down so the page does not
 * scroll: a laptop's 768 or 720 less its browser (`AUTH_SHORT`), and anything
 * under 700 — a 1280 × 600 window, a phone with its bars showing
 * (`AUTH_SHORTER`). The controls keep their 44px; only the air between them
 * gives.
 */
export const AUTH_SHORT = '@media (min-width: 600px) and (max-height: 800px)';
export const AUTH_SHORTER = '@media (max-height: 700px)';

/** The field of an auth form: the label above it, 44px tall, radius 6. */
export const authFieldSx = (theme) => {
  const accent = accentOf(theme);
  const line = fieldLineOf(theme);
  return {
    '& .MuiOutlinedInput-root': {
      boxSizing: 'border-box',
      minHeight: AUTH_CONTROL,
      px: '12px',
      py: 0,
      border: `1px solid ${line}`,
      borderRadius: `${RADIUS.control}px`,
      bgcolor: 'background.paper',
      color: 'text.primary',
      fontFamily: UNIVERSE_FONT,
      fontSize: 14,
      fontWeight: 500,
      transition: `border-color ${MOTION.fast}ms, box-shadow ${MOTION.fast}ms`,
      '&:hover': { borderColor: alpha(accent, 0.5) },
      '&.Mui-focused': {
        borderColor: accent,
        boxShadow: `0 0 0 3px ${alpha(
          accent,
          theme.palette.mode === 'light' ? 0.22 : 0.3
        )}`
      },
      '&.Mui-error': { borderColor: theme.palette.error.main }
    },
    '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      height: AUTH_CONTROL - 2,
      p: 0,
      fontFamily: UNIVERSE_FONT,
      fontSize: 14,
      fontWeight: 500,
      lineHeight: `${AUTH_CONTROL - 2}px`,
      color: 'text.primary',
      '&::placeholder': {
        color: theme.palette.text.secondary,
        fontWeight: 400,
        opacity: 1
      }
    },
    // §16: the label sits ABOVE the control, never floating in it. The
    // selector is doubled on purpose — `RHFTextField` paints a grey on
    // `.MuiInputLabel-root` after this object is spread, and at equal
    // specificity the later rule would win.
    '& .MuiInputLabel-root.MuiInputLabel-root': {
      position: 'static',
      transform: 'none',
      maxWidth: 'none',
      mb: 1,
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.3,
      color: 'text.primary',
      '&.Mui-focused, &.Mui-error': { color: 'text.primary' }
    },
    '& .MuiInputAdornment-positionEnd': { ml: 0.5, mr: -0.5 },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
      p: 0.5,
      color: 'text.secondary',
      '&:hover': { color: accent, bgcolor: 'transparent' }
    },
    '& .MuiFormHelperText-root': {
      mx: 0,
      mt: 0.5,
      fontFamily: UNIVERSE_FONT,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4
    },
    // Chrome paints its own autofill background; keep it on the theme surface.
    '& input:-webkit-autofill': {
      transition: 'background-color 9999s ease-in-out 0s',
      WebkitTextFillColor: theme.palette.text.primary
    }
  };
};

/**
 * The one primary action of an auth screen: the large pill of §17, the navy
 * fill on paper and the gradient's midpoint on the night, lifting a pixel
 * under the pointer. Not the gradient — the gradient belongs to the panel
 * beside it, and when both are gradients nothing leads.
 */
export const authButtonSx = (theme) => ({
  ...primaryButtonSx(theme),
  // a height, not a minimum: the large button's own padding is what made it
  // 48 beside 40px fields
  height: AUTH_CONTROL,
  minHeight: AUTH_CONTROL,
  py: 0,
  px: '20px',
  fontSize: 15
});

/** The second action, when one deserves a button: the same footprint, outlined. */
export const authSecondaryButtonSx = (theme) => ({
  ...secondaryButtonSx(theme),
  height: AUTH_CONTROL,
  minHeight: AUTH_CONTROL,
  py: 0,
  px: '20px',
  fontSize: 15
});

/**
 * "Continue with Microsoft": the secondary footprint with the provider's own
 * mark before the words — the mark is fixed artwork (its four squares carry
 * their own fills, so the hover recolours the words and never the logo).
 * Leaving for Microsoft is a STATE, not a disabled button: while it lasts the
 * words read "Redirecting to Microsoft…" at full opacity in the quiet ink.
 */
export const authProviderButtonSx = (theme) => ({
  ...authSecondaryButtonSx(theme),
  justifyContent: 'center',
  '& .MuiButton-startIcon': { mr: 1.5, ml: 0 },
  '&.MuiLoadingButton-loading.Mui-disabled': {
    opacity: 1,
    color: theme.palette.text.secondary
  }
});

/**
 * The line between two ways in: a hairline each side of a short phrase in the
 * quiet ink ("or sign in with email"). A divider, not a heading — it names the
 * choice and steps back.
 */
export const authDividerSx = (theme) => ({
  fontFamily: UNIVERSE_FONT,
  fontSize: 12.5,
  fontWeight: 500,
  lineHeight: 1.4,
  color: 'text.secondary',
  '&::before, &::after': { borderTopColor: hairlineOf(theme) },
  '& .MuiDivider-wrapper': { px: 1.5 }
});

/** The quiet way out of a form: "Forgot password?", "Sign in with a link". */
export const authLinkSx = (theme, { quiet = false } = {}) => ({
  fontFamily: UNIVERSE_FONT,
  fontSize: 13,
  fontWeight: quiet ? 500 : 600,
  lineHeight: 1.4,
  color: quiet ? 'text.secondary' : accentOf(theme),
  borderRadius: '4px',
  '&:hover': { color: accentOf(theme) },
  '&:focus-visible': focusRingSx(theme)
});
